import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PasswordInput } from '../components/AuthCard';
import { api, type ApiError } from '../lib/api';

const MIN_LENGTH = 10;

/**
 * Lands here from the emailed link. No session is issued on success — the link
 * can sit in an inbox for an hour, so the new password gets typed once on the
 * sign-in page instead.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: password }),
      });
      setDone(true);
    } catch (err) {
      setError((err as ApiError).message);
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center sm:px-6">
        <h1 className="font-display text-2xl font-semibold text-ink-900">
          That link is incomplete
        </h1>
        <p className="mt-4 text-ink-500">
          Open the link from your email again, or request a new one.
        </p>
        <Link
          to="/forgot-password"
          className="mt-9 inline-flex rounded-lg bg-ink-800 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-ink-900"
        >
          Request a new link
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center sm:px-6">
        <h1 className="font-display text-2xl font-semibold text-ink-900">Password updated</h1>
        <p className="mt-4 text-ink-500">
          You can sign in with your new password now. Any other devices that were signed in have
          been signed out.
        </p>
        <Link
          to="/login"
          className="mt-9 inline-flex rounded-lg bg-ink-800 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-ink-900"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16 sm:px-6 lg:py-24">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
        Set a new password
      </h1>
      <p className="mt-3 text-[0.9375rem] text-ink-500">
        Use at least {MIN_LENGTH} characters.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-ink-900">
            New password
          </label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2"
          />
        </div>

        <div>
          <label htmlFor="confirm" className="block text-sm font-medium text-ink-900">
            Confirm new password
          </label>
          <PasswordInput
            id="confirm"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-2"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-ink-800 px-6 py-3.5 text-base font-medium text-white transition-colors hover:bg-ink-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Save new password'}
        </button>
      </form>
    </div>
  );
}
